import { Epic } from 'redux-observable'
import { filter, ignoreElements, map, mergeMap, pluck, tap } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { uiEvents, UiEvent } from 'modules/ui'
import { ROUTES } from 'constants/routes'
import { EpicDependencies, RootState } from 'src/state/types'
import { setItem } from 'utils/localStorage'
import { ENDPOINTS } from './constants'
import { events, IntegrationsEvent } from './state'
import {
  TIntegration,
  TIntegrationOauthDetailsResponse,
  TTpnAuthorizeResponse,
  TTpnCanAccessCredexResponse,
  TTpnConsumerEnquiryHistoryResponse,
  TTpnConsumerEnquiryResponse,
  TTpnConsumerModule,
} from './types'

export const apiFetchIntegrations: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.integrations_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INTEGRATIONS_LIST, state$, {}).pipe(
        pluck('response'),
        map((res: TIntegration[]) => events.integrations_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiFetchIntegrations'),
  )

/**
 * Navigates the browser to the provider's OAuth consent screen, so it deliberately emits
 * nothing — the redirect ends this document's life.
 */
export const apiFetchIntegrationOauthDetails: Epic<
  IntegrationsEvent,
  IntegrationsEvent,
  RootState,
  EpicDependencies
> = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.integrationOauthDetails_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INTEGRATION_OAUTH_DETAILS, state$, action.payload).pipe(
        pluck('response'),
        tap((res: TIntegrationOauthDetailsResponse) => {
          setItem('state', res.state)
          setItem('syncExistingProperties', action.payload.syncExistingProperties)
          window.location.href = `${res.url}&redirect_uri=${window.location.origin}/integrations`
        }),
        ignoreElements(),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiFetchIntegrationOauthDetails'),
  )

export const apiActivateIntegration: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.activateIntegration_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.ACTIVATE_INTEGRATION, state$, action.payload, {}).pipe(
        pluck('response'),
        map(res => events.activateIntegration_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiActivateIntegration'),
  )

export const apiDeactivateIntegration: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { remove, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.deactivateIntegration_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.DEACTIVATE_INTEGRATION, state$, action.payload).pipe(
        pluck('response'),
        map(res => events.deactivateIntegration_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiDeactivateIntegration'),
  )

export const apiActivatePropDataIntegration: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.activatePropDataIntegration_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.INTEGRATIONS_BASE, state$, action.payload, {}).pipe(
        pluck('response'),
        map(res => events.activatePropDataIntegration_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiActivatePropDataIntegration'),
  )

export const apiRefreshPropDataToken: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.refreshPropDataToken_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.PROP_DATA_TOKEN_REFRESH, state$, action.payload, {}).pipe(
        pluck('response'),
        mergeMap(res => [events.refreshPropDataToken_success(res), events.refreshPropDataIntegration_request()]),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiRefreshPropDataToken'),
  )

export const apiRefreshPropDataIntegration: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.refreshPropDataIntegration_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.PROP_DATA_REFRESH_INTEGRATION, state$, action.payload, {}).pipe(
        pluck('response'),
        map(res => events.refreshPropDataIntegration_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiRefreshPropDataIntegration'),
  )

/** Also emits a cross-module `ui/redirect`, hence the widened event union. */
export const apiTpnAuthorize: Epic<
  IntegrationsEvent | UiEvent,
  IntegrationsEvent | UiEvent,
  RootState,
  EpicDependencies
> = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.tpnAuthorize_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.TPN_AUTHORIZE, state$, action.payload, {}).pipe(
        pluck('response'),
        mergeMap((res: TTpnAuthorizeResponse) => [
          events.tpnAuthorize_success({ ...res }),
          uiEvents.redirect(ROUTES.integrations),
          events.tpnConsumerCanAccessCredex_request(),
        ]),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiTpnAuthorize'),
  )

export const apiTpnConsumerCanAccessCredex: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tpnConsumerCanAccessCredex_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.TPN_CONSUMER_CAN_ACCESS_CREDEX, state$, {}).pipe(
        pluck('response'),
        map((res: TTpnCanAccessCredexResponse) => events.tpnConsumerCanAccessCredex_success({ ...res })),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiTpnConsumerCanAccessCredex'),
  )

export const apiTpnConsumerEnquiry: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tpnConsumerEnquiry_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.TPN_CONSUMER_ENQUIRY, state$, action.payload, {}).pipe(
        pluck('response'),
        map((res: TTpnConsumerEnquiryResponse) => events.tpnConsumerEnquiry_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiTpnConsumerEnquiry'),
  )

export const apiTpnConsumerEnquiryHistory: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tpnConsumerEnquiryHistory_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.TPN_CONSUMER_ENQUIRY_HISTORY, state$, action.payload).pipe(
        pluck('response'),
        map((res: TTpnConsumerEnquiryHistoryResponse) => events.tpnConsumerEnquiryHistory_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiTpnConsumerEnquiryHistory'),
  )

export const apiTpnConsumerModules: Epic<IntegrationsEvent, IntegrationsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tpnConsumerModules_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.TPN_CONSUMER_MODULES, state$, action.payload).pipe(
        pluck('response'),
        map((res: TTpnConsumerModule[]) => events.tpnConsumerModules_success(res)),
        catchRestError(action),
      ),
    ),
    tag('integrations/epics/apiTpnConsumerModules'),
  )
